import { type Link as CauldronLink } from '@deque/cauldron-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAxeAccountAnalytics } from '../contexts/analytics';

export type NavigateFn = (url: string) => void;

interface Props extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string;
  // Override the `navigate` implementation (useful for testing).
  navigate?: NavigateFn;
  variant?: 'button' | 'button-secondary';
  thin?: boolean;
  sendClickEvent?: () => void;
  children: React.ReactNode;
  as?: 'a' | typeof CauldronLink;
}

/**
 * Default `navigate()` implementation.
 */

export const defaultNavigate: NavigateFn = url => {
  window.location.href = url;
};

interface AnalyticsLinkHandlerOptions {
  href: string;
  sendClickEvent?: () => void | Promise<void>;
  navigate?: NavigateFn;
}

/**
 * Build the click / aux-click handlers for an analytics-instrumented link.
 * On a primary click the analytics call is flushed (awaited) before
 * navigation proceeds, so a same-tab navigation can't cancel the in-flight
 * event; modifier and non-primary clicks open a new tab and keep the current
 * page alive, so analytics is fire-and-forget there.
 *
 * Shared by `AnalyticsEnabledLink` and by call sites that must render their
 * own anchor element rather than be wrapped — e.g. a Cauldron
 * `ActionListLinkItem`, which has to be a direct `ActionList` child for the
 * menu's roles/keyboard handling and so cannot be nested inside this
 * component. Reusing these handlers keeps every instrumented link on one
 * flush-safe implementation.
 */
export const analyticsLinkHandlers = ({
  href,
  sendClickEvent,
  navigate = defaultNavigate
}: AnalyticsLinkHandlerOptions) => {
  // Analytics is best-effort: swallow a rejected/failed send (e.g. Amplitude
  // returns non-200) so it never aborts navigation or surfaces as an
  // unhandled promise rejection.
  const fireAndForget = (): void => {
    if (sendClickEvent) {
      void Promise.resolve(sendClickEvent()).catch(() => {});
    }
  };

  const onClick = async (
    e: React.MouseEvent<HTMLAnchorElement>
  ): Promise<void> => {
    // Preserve native link semantics for modifier / non-primary clicks
    // (cmd/ctrl-click, shift-click, alt-click, middle-click) — the browser
    // opens a new tab/window and keeps the current page alive, so there's
    // no flush-race to fix. Fire-and-forget the analytics call and let the
    // default navigation proceed.
    const isModifiedClick =
      e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0;
    if (isModifiedClick) {
      fireAndForget();
      return;
    }
    // Primary same-tab click: flush the event before navigating so the
    // in-flight request isn't cancelled by the unload — but never let an
    // analytics failure block navigation.
    e.preventDefault();
    if (sendClickEvent) {
      try {
        await sendClickEvent();
      } catch {
        // swallow — see fireAndForget
      }
    }
    navigate(href);
  };

  // Middle-click dispatches `auxclick`, not `click`, in real browsers
  // (Chromium/Firefox). jsdom is forgiving and fires `click` for any
  // `button`, but production middle-clicks would otherwise miss `onClick`
  // entirely. `auxclick` fires for any non-primary button (1=middle,
  // 2=right). For middle-click we want fire-and-forget analytics; for
  // right-click the context menu may or may not result in a navigation, so
  // we fire-and-forget too — recording intent is better than silently
  // dropping. Do not preventDefault — let the browser handle the gesture.
  const onAuxClick = (): void => {
    fireAndForget();
  };

  return { onClick, onAuxClick };
};

/**
 * Render a link (`<a>`) which sends analytics on click.
 *
 * Will gracefully degrade when unable to send events (eg Amplitude's servers are down, the user has an ad-blocker, etc.).
 */

const AnalyticsEnabledLink: React.FC<Props> = ({
  href,
  sendClickEvent,
  navigate = defaultNavigate,
  as = 'a',
  children,
  thin,
  ...props
}) => {
  const Link = as;

  const { onClick, onAuxClick } = analyticsLinkHandlers({
    href,
    sendClickEvent,
    navigate
  });

  const isButton =
    props.variant && ['button', 'button-secondary'].includes(props.variant);

  return (
    <Link
      {...props}
      href={href}
      onClick={onClick}
      onAuxClick={onAuxClick}
      thin={isButton && thin}
    >
      {children}
    </Link>
  );
};

export default AnalyticsEnabledLink;

interface CommonProps {
  children?: React.ReactNode;
  navigate?: NavigateFn;
}

/**
 * Render a link to our Privacy Policy page which sends analytics.
 */

export const PrivacyPolicyLink: React.ComponentType<CommonProps> = props => {
  const { t } = useTranslation();
  const children = props.children || t('Privacy Policy');
  const analytics = useAxeAccountAnalytics();
  return (
    <AnalyticsEnabledLink
      href="https://www.deque.com/privacy-policy/"
      sendClickEvent={() => analytics.viewPrivacyPolicy()}
      navigate={props.navigate}
    >
      {children}
    </AnalyticsEnabledLink>
  );
};

/**
 * Render a link to our Privacy Policy page which sends analytics.
 */

export const LicenseAgreementLink: React.ComponentType<CommonProps> = props => {
  const { t } = useTranslation();
  const children = props.children || t('Terms of Service');
  const analytics = useAxeAccountAnalytics();
  return (
    <AnalyticsEnabledLink
      href="https://www.deque.com/terms-of-use/axe-devtools-online/"
      sendClickEvent={() => analytics.viewLicenseAgreement()}
      navigate={props.navigate}
    >
      {children}
    </AnalyticsEnabledLink>
  );
};
